import * as React from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { Button } from "@/components/ui/button";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { ScrollArea } from "@/components/ui/scroll-area";
import { tabsListVariants, tabsTriggerVariants } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/**
 * Layout variants. `vertical` puts the nav in a column on the left of the
 * section. `horizontal` stacks them: a page-size heading, the nav as a row
 * of tabs under it (it sticks to the top while the section scrolls), then
 * the section across the full width. On mobile both show the nav as a
 * sideways-scrolling row. The variant is set once on <SettingsLayout>; the
 * SettingsNav and SettingsNavItems inside read it from context.
 */
const settingsLayoutVariants = cva(
  "mx-auto flex max-w-6xl flex-col md:p-3 [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]",
  {
    variants: {
      variant: {
        vertical: "space-y-8 md:flex-row",
        horizontal: "",
      },
    },
    defaultVariants: { variant: "vertical" },
  },
);

type SettingsLayoutVariant = NonNullable<VariantProps<typeof settingsLayoutVariants>["variant"]>;

const SettingsLayoutVariantContext = React.createContext<SettingsLayoutVariant>("vertical");

/**
 * The nav: in `vertical`, a column sticky on the left, a sideways-scrolling
 * row of tabs on mobile (no scrollbar bar under it); in `horizontal`, a row
 * of tabs under a large heading. `title` heads it (desktop only in
 * `vertical`), `action` (an add button) sits beside the title. Items are
 * SettingsNavItems.
 */
export function SettingsNav({
  title = "Settings",
  action,
  children,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  const variant = React.useContext(SettingsLayoutVariantContext);

  if (variant === "horizontal") {
    return (
      <HorizontalSettingsNav title={title} action={action}>
        {children}
      </HorizontalSettingsNav>
    );
  }

  return (
    <nav className="sticky top-0 z-10 flex h-min w-full flex-row gap-1 overflow-auto border-b bg-background p-3 scrollbar-none md:top-3 md:max-w-64 md:flex-col md:border-none">
      <div className="mb-3.5 hidden w-full items-center border-b pb-1 md:flex">
        <h2 className="text-base font-medium">{title}</h2>
        {action && <div className="ml-auto flex items-center">{action}</div>}
      </div>
      {children}
    </nav>
  );
}

/** Nav item icons, both variants: dimmed, full strength on the open item and on hover. */
const NAV_ITEM_ICON =
  "[&_svg]:shrink-0 [&_svg]:opacity-60 data-[state=active]:[&_svg]:opacity-100 hover:[&_svg]:opacity-100";

/** How tall the fade under the stuck tab row grows — the ScrollArea's edge fade size. */
const NAV_FADE_PX = 32;

/**
 * The horizontal nav: the heading, then the tab row, which sticks to the top
 * while the section scrolls. The heading and the row are siblings in the
 * layout's column, so the row sticks for the whole page (a sticky child of a
 * wrapper would only stick within the wrapper).
 *
 * The ScrollArea's top edge fade would cover the stuck row (it masks the top
 * of the whole viewport), so SettingsLayout turns it off in this variant and
 * the row draws its own: a background-to-transparent band just under its
 * border, as tall as the content scrolled beneath the row (up to
 * NAV_FADE_PX) — nothing while the heading is still on screen.
 */
function HorizontalSettingsNav({
  title,
  action,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  const headingRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const heading = headingRef.current;
    const row = rowRef.current;
    const viewport = row?.closest<HTMLElement>("[data-slot=scroll-area-viewport]");
    if (!heading || !row || !viewport) return;

    // Content under the row = how far the heading's bottom has gone above the
    // viewport's top. Written straight to the style: no re-render per scroll.
    const update = () => {
      const under = viewport.getBoundingClientRect().top - heading.getBoundingClientRect().bottom;
      row.style.setProperty("--settings-nav-fade", `${Math.min(NAV_FADE_PX, Math.max(0, under))}px`);
    };

    update();
    viewport.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    return () => {
      viewport.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  return (
    <>
      <div ref={headingRef} className="flex items-center gap-2 px-3 pt-3 pb-4 md:pt-6">
        <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
        {action && <div className="ml-auto flex items-center">{action}</div>}
      </div>
      {/* The sticky wrapper holds the fade: the nav itself scrolls sideways
          on mobile, and its overflow would clip anything hung below it. */}
      <div ref={rowRef} className="sticky top-0 z-10 [--settings-nav-fade:0px]">
        {/* The underline Tabs list: its py-2 is where the active bar hangs,
            so the bar lands on this border. */}
        <nav
          className={cn(
            tabsListVariants({ variant: "underline" }),
            "w-full justify-start overflow-auto border-b bg-background scrollbar-none md:overflow-visible",
          )}
        >
          {children}
        </nav>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-full h-(--settings-nav-fade) bg-linear-to-b from-background to-transparent"
        />
      </div>
    </>
  );
}

/**
 * One nav entry — a small Button, secondary while its section is open; in
 * `horizontal`, an underline tab (the look of `<TabsList variant="underline">`,
 * primary with a bar under it while its section is open). Pass your router's
 * link with `asChild` so the entry IS the link:
 * `<SettingsNavItem asChild active><Link to="…"><Icon />Label</Link></SettingsNavItem>`.
 */
export function SettingsNavItem({
  active = false,
  className,
  ...props
}: React.ComponentProps<typeof Button> & { active?: boolean }) {
  const layout = React.useContext(SettingsLayoutVariantContext);

  if (layout === "horizontal") {
    // Button's own props have no meaning on a tab; the rest go to the element.
    const { asChild, variant: _variant, size: _size, scope: _scope, scopeColor: _scopeColor, card: _card, ...rest } = props;
    const Comp = asChild ? Slot : "button";
    return (
      <ScopeWrapper className="shrink-0">
        <Comp
          {...(asChild ? {} : { type: "button" as const })}
          data-state={active ? "active" : "inactive"}
          className={cn(
            tabsTriggerVariants({ variant: "underline" }),
            "gap-1.5 [&_svg]:-ms-0.5 [&_svg:not([class*='size-'])]:size-4",
            NAV_ITEM_ICON,
            className,
          )}
          {...rest}
        />
      </ScopeWrapper>
    );
  }

  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      size="sm"
      data-state={active ? "active" : "inactive"}
      className={cn(
        "w-full transition-none md:justify-start",
        NAV_ITEM_ICON,
        active ? "hover:bg-muted" : "font-normal text-foreground/80 hover:bg-sidebar",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A settings-style page — the org's Settings, a campaign's Settings and its
 * Pipeline: the nav (a SettingsNav) on the left, the open section beside it,
 * both centred — or, with `variant="horizontal"`, a large heading and the nav
 * as a row of tabs above the section.
 * It scrolls in its own ScrollArea (overlay scrollbar + edge fade): a native
 * scrollbar came and went with each section's height and shifted the layout
 * sideways. Radix wraps the content in a display:table div — forced to block
 * so widths and truncation behave.
 *
 * Switching sections animates here, once for every page: the old section
 * fades and slides out, then the new one fades and slides in (sections carry
 * no animation of their own). The page must stay mounted across sections for
 * that — CampaignPage keys its Settings and Pipeline tabs per campaign.
 *
 * @example
 * import { useState } from "react"
 * import { Bell, User } from "lucide-react"
 * import { SettingsLayout, SettingsNav, SettingsNavItem } from "@/components/ui/settings-layout"
 *
 * const SECTIONS = [
 *   { key: "profile", label: "Profile", icon: User },
 *   { key: "notifications", label: "Notifications", icon: Bell },
 * ]
 *
 * export function SettingsPage() {
 *   const [section, setSection] = useState("profile")
 *   return (
 *     <SettingsLayout
 *       sectionKey={section}
 *       nav={
 *         <SettingsNav title="Settings">
 *           {SECTIONS.map(({ key, label, icon: Icon }) => (
 *             <SettingsNavItem key={key} active={key === section} onClick={() => setSection(key)}>
 *               <Icon />
 *               {label}
 *             </SettingsNavItem>
 *           ))}
 *         </SettingsNav>
 *       }
 *     >
 *       {section === "profile" ? <p>Profile settings</p> : <p>Notification settings</p>}
 *     </SettingsLayout>
 *   )
 * }
 */
export function SettingsLayout({
  nav,
  sectionKey,
  variant,
  children,
}: {
  nav: ReactNode;
  /** The open section's key — a new key plays the exit and enter animations. */
  sectionKey: string;
  children: ReactNode;
} & VariantProps<typeof settingsLayoutVariants>) {
  const resolved = variant ?? "vertical";
  return (
    <SettingsLayoutVariantContext.Provider value={resolved}>
      <ScrollArea
        scrollFade
        className={cn(
          "h-full [&_[data-slot=scroll-area-viewport]>div]:block!",
          // Horizontal: no top edge fade — it would cover the stuck tab row,
          // which draws its own fade under itself (HorizontalSettingsNav).
          resolved === "horizontal" && "*:data-[slot=scroll-area-viewport]:mask-t-from-100%",
        )}
      >
        <div data-variant={resolved} className={settingsLayoutVariants({ variant: resolved })}>
          {nav}
          <div
            className={cn(
              "h-auto w-full overflow-auto p-3 [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]",
              resolved === "horizontal" && "pt-6",
            )}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={sectionKey}
                className="space-y-8"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.12, ease: "easeOut" } }}
                exit={{ opacity: 0, y: 8, transition: { duration: 0.08, ease: "easeIn" } }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </ScrollArea>
    </SettingsLayoutVariantContext.Provider>
  );
}
